import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/base/Layout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, articles } from '@/mocks/data';

function ArticleSEO({ article }: { article: typeof articles[0] }) {
  return (
    <SEO
      title={`${article.title} - ${siteInfo.name}`}
      description={article.excerpt}
      keywords={`${article.category},体育资讯,满冠体育,${article.title.substring(0, 30)}`}
      canonicalUrl={`${siteInfo.url}/article/${article.slug}`}
      ogType="article"
      ogImage={article.image}
      structuredData={{
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.image,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            publisher: {
              '@type': 'Organization',
              name: siteInfo.name,
              logo: { '@type': 'ImageObject', url: `${siteInfo.url}/logo.png` },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${siteInfo.url}/article/${article.slug}`,
            },
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
              { '@type': 'ListItem', position: 2, name: '体育资讯', item: `${siteInfo.url}/articles` },
              { '@type': 'ListItem', position: 3, name: article.title, item: `${siteInfo.url}/article/${article.slug}` },
            ],
          },
        ],
      }}
    />
  );
}

const articleFullContent: Record<string, string[]> = {
  'marathon-training-guide-2026': [
    '随着秋季马拉松赛季的临近，越来越多的跑步爱好者开始投入到紧张的备战训练中。无论你是第一次挑战全程马拉松的新手，还是希望刷新个人最好成绩的老将，科学系统的训练方案都是成功完赛的关键。',
    '基础训练阶段（第1-4周）：本阶段的核心目标是建立有氧基础。建议每周进行4-5次跑步训练，包括3次45-60分钟的轻松跑、1次间歇跑训练和1次长距离慢跑（LSD）。轻松跑时保持能够正常交谈的强度，心率控制在最大心率的65-75%。间歇跑可以有效提升最大摄氧量，推荐400米×8组的训练方案。',
    '强化训练阶段（第5-8周）：进入强化期后，训练强度和跑量逐步提升。周跑量应达到目标比赛距离的1.5-2倍。增加1次节奏跑训练（以半马比赛配速持续跑20-30分钟），长距离跑延长至25-32公里。同时开始模拟比赛补给策略，每5公里补充一次能量胶或电解质饮料。',
    '调整减量阶段（第9-10周）：赛前2-3周开始逐步减少训练量，让身体充分恢复和储备能量。周跑量递减至峰值的60-70%，最后一次长距离跑安排在赛前2周完成。保持一定的速度训练以维持肌肉记忆。',
    '饮食营养建议：马拉松训练期间，碳水化合物摄入应占总热量的60-65%，优质蛋白占15-20%。训练前2-3小时进食易消化的碳水化合物，训练后30分钟内补充蛋白质和碳水化合物帮助恢复。赛前3天开始进行"糖原填充法"，逐步提高碳水化合物比例至80%。',
    '常见问题与注意事项：训练中如出现持续疼痛应立即停止并就医；每周至少安排1-2天完全休息；确保每晚7-9小时充足睡眠；选择合适的跑鞋并定期更换（建议每500-800公里）。',
  ],
  'youth-basketball-development': [
    '青少年篮球培训在中国正经历一场深刻的变革。传统的"经验式"教学方法正在被科学化、数据化的现代训练体系所取代。这一转变不仅提升了培训效果，更为中国篮球的未来发展奠定了坚实的基础。',
    '数据驱动训练成为新趋势：越来越多的培训机构开始引入运动追踪技术和数据分析系统。通过GPS追踪器、心率监测设备和视频分析软件，教练能够精准记录每位学员的运动轨迹、跑动距离、心率变化和技术动作细节。这些数据为个性化训练方案的制定提供了科学依据。',
    '心理素质培养受到重视：现代青训体系不再仅仅关注技术和体能，心理素质的培养被提升到了同等重要的位置。通过心理辅导课程、团队建设活动和比赛模拟训练，帮助青少年运动员建立自信、学会压力管理和团队协作。研究表明，心理素质优秀的运动员在比赛中发挥更稳定。',
    '营养和康复科学融入日常训练：专业的运动营养师和康复师开始加入培训团队。根据每位学员的身体状况和训练强度，制定个性化的饮食计划和恢复方案，包括拉伸训练、泡沫轴放松和冷热交替浴等科学恢复手段。',
    '多元化发展路径：不再以"成为职业球员"作为唯一目标。现代培训体系关注学员的全面发展，通过篮球运动培养领导力、团队精神和坚韧品格。即使不走上职业道路，这些品质也将使学员受益终身。',
    '家校协同机制：建立教练与家长之间的定期沟通机制，通过月度评估报告、家长观摩日和家庭运动任务，将培训从球场延伸到家庭，形成良性互动的培养生态。',
  ],
  'sports-equipment-selection': [
    '在体育用品市场不断升级的今天，面对琳琅满目的品牌和产品，如何做出明智的消费决策？我们采访了多位国家级运动员和资深教练，为你带来最实用的装备选购指南。',
    '跑鞋：适合的才是最好的。国家队田径教练张指导强调，选购跑鞋最重要的不是品牌和价格，而是与自身足型和跑步姿势的匹配度。建议在专业运动用品店进行步态分析，了解自己的足弓类型（正常、扁平或高足弓）和跑步姿势（内翻、外翻或正常）。扁平足跑者应选择稳定性跑鞋，高足弓跑者则更适合缓震型跑鞋。',
    '羽毛球拍：平衡点是关键。前羽毛球国手李教练分享，羽毛球拍的平衡点（头重、均衡或头轻）直接影响击球感觉。进攻型选手适合头重拍，能够产生更大的杀球力量；防守型选手则应选择头轻拍，挥拍更快更灵活。对于业余爱好者，建议从均衡型球拍开始。',
    '运动手表：功能需匹配需求。运动科技专家建议，不要盲目追求最贵的旗舰型号。如果你只是日常跑步和健身，带GPS和心率监测的中端型号就完全足够。铁人三项爱好者则需要防水性能更好、支持多运动模式的专业型号。关键功能包括GPS精度、心率监测准确性和电池续航。',
    '压缩装备：科学使用效果更佳。压缩衣、压缩袜近年来备受追捧。运动医学研究表明，适度压缩可以促进血液循环、减少肌肉震动，但并非越紧越好。选购时应注意梯度压缩设计（从远端到近端压力递减），并在运动后持续穿着1-2小时以加速恢复。',
    '预算分配策略：对于入门运动爱好者，建议将60%的预算投入到最重要的装备上（如跑鞋对于跑步者），30%用于基础服装，10%用于配件。随着运动水平的提升，再逐步升级其他装备，避免一次性过度消费。',
  ],
  'sports-injury-prevention': [
    '运动损伤是每一位运动爱好者都可能面临的问题。根据运动医学统计，超过70%的运动损伤源于训练方法不当或预防意识不足。掌握以下五个关键原则，能够显著降低受伤风险。',
    '原则一：充分热身，循序渐进。运动前15-20分钟的动态热身是预防损伤的第一道防线。应包括5分钟低强度有氧运动（如慢跑或跳绳）、动态拉伸（如高抬腿、侧向移动）和针对性的激活训练（如核心激活、臀肌激活）。热身后的肌肉温度升高，弹性和反应速度都显著提升。',
    '原则二：技术动作是第一防线。刘博士（运动科学博士）指出，绝大多数跑步膝、网球肘、游泳肩等慢性损伤，根源都在于技术动作的错误。建议初学者在开始任何新运动时，先花时间掌握正确的技术动作，而不是急于提升强度和距离。可以利用手机录像功能，对比专业运动员的动作进行分析。',
    '原则三：合理分配训练负荷。"10%原则"是运动训练中广泛应用的法则：每周训练量（时间或距离）的增加不应超过10%。此外，每3-4周的训练后应安排一个"恢复周"，将训练量降至平时的50-60%。这种"超量恢复"的训练模式能让身体充分适应并变得更强大。',
    '原则四：力量训练是防伤基石。强大的肌肉群能够有效保护关节和韧带。每周建议进行2-3次功能性力量训练，重点加强核心肌群、臀部肌群和肩胛稳定肌群。单腿训练（如单腿深蹲、弓步走）尤为重要，能够改善平衡能力和运动中的身体控制。',
    '原则五：倾听身体信号，及时处理。持续性疼痛、关节肿胀、活动范围受限都是身体发出的警告信号。遇到这些情况应立即停止运动，采取RICE原则（休息、冰敷、加压、抬高）进行初步处理。如果48小时内症状没有明显改善，务必寻求专业运动医学医师的帮助。',
  ],
  'swimming-technique-improvement': [
    '自由泳是游泳运动中最常见、速度最快的泳姿。许多游泳爱好者在达到一定水平后，往往会遇到速度瓶颈：无论怎么努力，配速都无法进一步提升。问题的根源通常出在技术细节上。本文将为你详细解析自由泳的三大核心技术要点。',
    '核心要点一：身体位置与流线型。水的密度约是空气的800倍，减小阻力比增加推进力更加高效。理想的自由泳身体位置应保持水平，头部与脊柱成一直线，目光看向池底而非前方。常见的错误是抬头过高导致髋部下沉，使得身体在水中呈现"V"形，阻力急剧增加。练习方法：进行侧身打水练习，感受身体浮在水面上的感觉。',
    '核心要点二：高效的划水技术。自由泳的推进力85%来自手臂划水。正确的划水可以分为四个阶段：入水与伸展（手臂前伸，指尖先入水）、抓水（手腕微屈，形成高肘姿势）、推水（前臂和手掌向后方推水至大腿侧）和恢复（肘部引领手臂出水向前移动）。最关键的是保持"高肘划水"，像用手臂画一个钥匙孔形状的轨迹。',
    '核心要点三：呼吸时机与节奏。很多游泳者的速度瓶颈源于不合理的呼吸技术。正确的呼吸应该在划水恢复阶段进行，头部随身体自然转动（而非抬起），一侧泳镜保持在水下。呼吸节奏建议采用2划一吸或3划一吸（双侧呼吸），双侧呼吸能够保持身体平衡，避免产生"死点"。',
    '辅助练习方案：每周安排2-3次技术专项练习，每次30分钟。推荐以下练习组合：扶板打水200米×4组（强化打腿）；单臂划水100米×6组（每侧各3组，感受抓水推水）；握拳游50米×8组（培养前臂水感）。坚持4周后，你会发现配速有明显的提升。',
    '国家级游泳教练李教练的额外建议：定期录制自己的游泳视频进行分析非常重要。很多时候我们感觉自己的动作是正确的，但视频会揭示真实的差距。可以利用手机防水壳加三脚架进行拍摄，对比优秀运动员的技术视频，找出需要改进的细节。',
  ],
  'corporate-sports-teambuilding': [
    '在现代企业管理中，团队建设的重要性不言而喻。然而，传统的聚餐、K歌、拓展训练等团建方式正在逐渐失去吸引力。越来越多的企业开始探索以体育运动为载体的新型团建模式，取得了意想不到的效果。',
    '为什么运动团建更有效？心理学研究表明，共同参与体育活动能够产生独特的"团队凝聚效应"。当团队成员一起经历体力挑战、共同为目标拼搏时，身体释放的内啡肽和多巴胺能够创造积极的情感联结。这种在汗水和欢笑中建立的信任关系，比在会议室里达成的共识更加牢固持久。',
    '迷你奥运会：沉浸式团队竞技。将团队分为多个小组，设计包含接力赛、拔河、趣味障碍跑等项目的"迷你奥运会"赛程。每个项目都需要团队成员的密切配合和策略制定。活动不仅考验体能，更考验沟通能力和领导力。获胜团队的荣誉感和仪式感，能够延续到日常工作中。',
    '户外运动挑战：突破舒适区。组织登山、定向越野或皮划艇等户外运动，让团队成员走出办公室，面对自然环境的挑战。这类活动能够快速暴露团队中的沟通问题和协作短板，为后续的团队建设提供针对性方向。同时，共同克服困难的成功体验，会极大增强团队的信心。',
    '运动健康挑战赛：培养长期习惯。以月或季度为单位，发起全员运动打卡挑战。利用运动APP记录步数、跑步距离或运动时长，设置团队排行榜和奖励机制。这种方式不仅能改善员工的健康状况，还能在日常工作中持续培养团队协作意识。',
    '满冠体育企业团建服务：我们为企业客户提供定制化的运动团建方案，从场地选择、项目设计到现场执行，全程由专业教练和赛事策划团队负责。无论是50人的小型团队活动还是500人的大型运动会，我们都能提供一站式的完美体验。',
  ],
};

const relatedArticles = [
  { title: '运动损伤防护指南', slug: 'sports-injury-prevention' },
  { title: '装备选购秘籍', slug: 'sports-equipment-selection' },
  { title: '游泳技术突破', slug: 'swimming-technique-improvement' },
];

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const content = slug ? articleFullContent[slug] : null;

  if (!article || !content) {
    return (
      <Layout>
        <div className="pt-16 md:pt-18">
          <div className="container-custom py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-3 font-heading">文章未找到</h1>
            <Link to="/articles" className="text-primary-500 hover:text-primary-600">返回文章列表</Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ArticleSEO article={article} />

      <article className="pt-16 md:pt-18">
        <div className="bg-background-100">
          <div className="container-custom py-6 md:py-8">
            <Breadcrumb items={[
              { label: '体育资讯', to: '/articles' },
              { label: article.title },
            ]} />
          </div>
        </div>

        <div className="container-custom py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6 md:mb-8">
              <span className="inline-block px-3 py-1 bg-accent-500 text-background-50 rounded-md text-xs font-medium mb-3">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4 font-heading">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i> {article.readTime}
                </span>
              </div>
            </div>

            <div className="relative h-64 md:h-96 rounded-lg overflow-hidden mb-8 md:mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-custom">
              {content.map((paragraph, idx) => (
                <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-10 md:mt-12 pt-8 border-t border-background-200">
              <h3 className="text-lg font-bold text-foreground-900 mb-4 font-heading">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="block p-4 bg-background-100 rounded-lg hover:bg-background-200 transition-colors no-underline"
                  >
                    <span className="text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors">
                      {ra.title}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 rounded-lg text-sm font-medium hover:bg-background-200 transition-colors"
              >
                <i className="ri-arrow-left-line"></i> 返回文章列表
              </Link>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}